import { lazy, Suspense, useState } from 'react';
import { LayersIcon } from 'lucide-react';

import { StepKindIcon } from '@/shared/components/StepKindIcon';
import { phaseAccentClass } from '@/features/workflows';
import { cn } from '@/lib/utils';
import type { LibraryItemView } from '@/lib/api/generated/model';
import { withoutFrontMatter } from '../frontMatter';
import { useLibraryFile } from '../hooks/useLibraryFile';
import { otherFilesOf, ownFileOf, readableSize } from '../itemFiles';
import { workflowOutline } from '../workflowOutline';

// The renderer pulls in react-markdown and remark-gfm; the file preview keeps
// it behind a lazy boundary for the same reason, and most drawers never open.
const Markdown = lazy(() =>
  import('@/shared/components/Markdown').then((m) => ({ default: m.Markdown })),
);

const READING = (
  <p aria-busy="true" className="text-muted-foreground text-sm">
    Reading…
  </p>
);

/**
 * What an item actually says, before anybody installs it (spec 105 US1).
 *
 * An agent and a skill are documents, so they are rendered as documents, minus
 * the front matter the drawer already shows above. A workflow is a set of
 * steps, so it is listed as one — its definition is operating instructions for
 * the agent that runs it, not a description for the person deciding whether to
 * take it (FR-024, research R9).
 *
 * The item's own file opens first, and every other file it carries can be
 * opened from the list: a skill that ships examples and an agent that ships a
 * checklist are carrying half their meaning in those files, and listing them
 * without letting anybody read them was an odd place to stop.
 *
 * Nothing here can stop an install. A file that cannot be read, or a definition
 * this page cannot parse, costs one line (FR-026).
 */
export function ItemPreview({
  workspaceId,
  item,
  items = [],
  onOpenItem,
}: {
  workspaceId: string;
  item: LibraryItemView;
  /** The catalog, so a linked step can name the workflow it hands work to. */
  items?: readonly LibraryItemView[];
  /** Open another item's drawer — a linked step's target (spec 109 US1). */
  onOpenItem?: (item: LibraryItemView) => void;
}) {
  const own = ownFileOf(item);
  const [openPath, setOpenPath] = useState<string | undefined>(undefined);
  // The drawer is keyed by item, so this resets with it; the fallback keeps
  // the item's own file open until somebody picks another.
  const path = openPath ?? own;
  const { file, isPending, failed } = useLibraryFile(
    workspaceId,
    item.kind,
    item.key,
    path,
    item.digest,
  );
  const files = item.files ?? [];

  if (own === undefined) return null;

  // Only the item's own file gets its kind's treatment: a workflow's steps come
  // from its definition, not from whatever else sits beside it.
  const isOwnFile = path === own;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">What it says</h3>

      {isPending && READING}

      {failed && (
        <p className="text-muted-foreground text-sm">
          This file could not be read. The item can still be installed.
        </p>
      )}

      {file && isOwnFile && item.kind === 'workflow' && (
        <Steps
          definition={file.text}
          item={item}
          items={items}
          onOpenItem={onOpenItem}
        />
      )}

      {file && !(isOwnFile && item.kind === 'workflow') && (
        <Suspense fallback={READING}>
          <Markdown
            content={isOwnFile ? withoutFrontMatter(file.text) : file.text}
            linkPolicy="none"
            className="max-w-prose text-sm"
          />
        </Suspense>
      )}

      {file?.truncated && (
        <p className="text-muted-foreground text-xs">
          This is the beginning of a {readableSize(file.size)} file. Install it
          to read the rest in this workspace.
        </p>
      )}

      {/* Every file, not only the extra ones: knowing an item is three files
          rather than one is the point, and one row is one line (FR-025). */}
      <h3 className="mt-2 text-sm font-medium">Files</h3>
      <ul aria-label="Files" className="flex flex-col gap-0.5 text-xs">
        {[...files.filter((f) => f.path === own), ...otherFilesOf(item)].map(
          (f) => (
            <li key={f.path}>
              <button
                type="button"
                aria-current={f.path === path ? 'true' : undefined}
                onClick={() => {
                  setOpenPath(f.path);
                }}
                className={cn(
                  'focus-visible:ring-ring flex w-full items-center justify-between gap-4 rounded-sm px-1 py-0.5 text-left transition-colors focus-visible:ring-3 focus-visible:outline-none',
                  f.path === path
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:bg-accent/60',
                )}
              >
                <span className="min-w-0 truncate font-mono">{f.path}</span>
                <span className="shrink-0 tabular-nums">
                  {readableSize(f.size)}
                </span>
              </button>
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

/**
 * A workflow's phases and steps: how many, what they are, where it waits.
 *
 * Marked the way the workflow canvas marks them, not in a scheme of this
 * page's own: a phase takes the next of the four accents the token layer
 * defines, and a step takes the same icon the graph and the task list give it —
 * a bot for an agent step, a person for a gate. Somebody who has read a
 * workflow on the canvas should not have to learn a second language to read the
 * same workflow here, and the four accents are already contrast-verified in
 * both themes (docs/brand.md FD-13), so this adds colour without inventing any.
 *
 * Colour is never the only signal: the phase carries its name and an icon, and
 * a gate says "waits for a person" in words (workflow spec FR-036).
 */
function Steps({
  definition,
  item,
  items,
  onOpenItem,
}: {
  definition: string;
  item: LibraryItemView;
  items: readonly LibraryItemView[];
  onOpenItem?: (item: LibraryItemView) => void;
}) {
  const outline = workflowOutline(definition);
  if (outline === null) {
    return (
      <p className="text-muted-foreground text-sm">
        This workflow’s steps could not be read. It can still be installed.
      </p>
    );
  }

  return (
    <ol aria-label="Steps" className="flex flex-col gap-2.5">
      {outline.phases.map((phase, index) => (
        <li
          key={phase.id}
          className={cn('flex flex-col gap-1.5', phaseAccentClass(index))}
        >
          <div className="border-phase-accent/25 bg-phase-accent/12 flex items-center gap-1.5 rounded-md border px-2 py-1">
            <LayersIcon
              aria-hidden="true"
              className="text-phase-accent size-3 shrink-0"
              strokeWidth={1.75}
            />
            <span className="text-phase-accent min-w-0 truncate text-xs font-medium tracking-wide uppercase">
              {phase.name}
            </span>
            <span className="text-muted-foreground shrink-0 text-xs">
              · {phase.steps.length}{' '}
              {phase.steps.length === 1 ? 'step' : 'steps'}
            </span>
          </div>
          <ol className="border-phase-accent/40 text-muted-foreground ml-2 flex flex-col gap-1 border-l-2 pl-3 text-sm">
            {phase.steps.map((step) => (
              <li key={step.id} className="flex flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <StepKindIcon kind={step.kind} />
                  <span className="min-w-0 truncate">{step.name}</span>
                  {step.waitsForAPerson && (
                    <span className="text-muted-foreground shrink-0 text-xs">
                      waits for a person
                    </span>
                  )}
                </span>
                {step.links !== null && (
                  <LinkedTarget
                    target={step.links}
                    item={item}
                    items={items}
                    onOpenItem={onOpenItem}
                  />
                )}
              </li>
            ))}
          </ol>
        </li>
      ))}
    </ol>
  );
}

/**
 * Where a linked step hands its work, under the step (spec 109 US1).
 *
 * Named by the workflow's display name and opened from here when the library
 * holds it, so a family can be read one workflow at a time without leaving the
 * drawer; Back returns, because opening a drawer is a place (spec 108). A key
 * the library does not hold, and a link to this same workflow, are named but
 * not offered: there is nothing else to open. "Continues when its tasks are
 * done" says in words what a gate's "waits for a person" says for a person —
 * the step creates tasks there and resumes once every one is done (spec 097).
 */
function LinkedTarget({
  target,
  item,
  items,
  onOpenItem,
}: {
  target: string;
  item: LibraryItemView;
  items: readonly LibraryItemView[];
  onOpenItem?: (item: LibraryItemView) => void;
}) {
  const found = items.find((i) => i.kind === 'workflow' && i.key === target);
  const self = target === item.key;
  return (
    <span className="text-muted-foreground flex flex-wrap items-baseline gap-x-1.5 pl-5 text-xs">
      <span aria-hidden="true">→</span>
      {self ? (
        <span>this same workflow</span>
      ) : found && onOpenItem ? (
        <button
          type="button"
          aria-label={`Open ${found.name}`}
          onClick={() => {
            onOpenItem(found);
          }}
          className="text-foreground focus-visible:ring-ring rounded-sm underline underline-offset-4 focus-visible:ring-3 focus-visible:outline-none"
        >
          {found.name}
        </button>
      ) : found ? (
        <span className="text-foreground">{found.name}</span>
      ) : (
        <span>
          <span className="font-mono">{target}</span> · not in the library
        </span>
      )}
      <span>· continues when its tasks are done</span>
    </span>
  );
}
